import {Injectable, Injector} from '@angular/core';

import {Observable, Subject} from 'rxjs';
import {
  HttpService, JsonProperty
} from 'projects/core/src/public-api';
import {AppStateStore} from 'projects/desktop/src/app/data/app-state.store';
import {AppStateQuery} from 'projects/desktop/src/app/data/app-state.query';
import {Project} from 'projects/app-misc/src/app/modules/project-misc/data/project/project';
import {ProjectPricing} from './project-pricing';
import {PricingFilters} from './pricing-filters';
import {JsonObject} from 'json2typescript';
import {ResponcePage} from '../../../../../../core/src/lib/misc/data/responce-page';
import {PricingGroup} from './group/pricing-group';
import {PricingArea} from './pricing-area';
import {PricingGrade} from './pricing-grade';
import {PricingMiscItem} from './misc-item/pricing-misc-item';
import {PricingFormula} from './formula/pricing-formula';
import {PricingSubtotal} from './pricing-subtotal';
import {PricingSetting} from './pricing-setting';
import {PricingPrice} from './pricing-price';
import {PricingMiscItemPreset} from './misc-item/pricing-misc-item-preset';
import {PricingMiscItemGroup} from './misc-item/pricing-misc-item-group';
import {ProposalsInfo} from './proposals-info';
import {PricingGroupItem} from './group/pricing-group-item';
import {WsUpdateResponce} from '../../data/ws-update-responce';
import {PricingOptionShort} from './pricing-option-short';
import Handsontable from 'handsontable';

export class PricingFilter {
  project?: number[];
  users?: number[];
  customer?: number[];
  statuses?: number[];
  start?: number;
  limit?: number;
}

@JsonObject('PricingResponcePage')
export class PricingResponcePage extends ResponcePage {

  @JsonProperty([ProjectPricing], 'content')
  content: ProjectPricing[];
}

export class SumValue {
  val: number;
  qty: number;
}

@Injectable({
  providedIn: 'root',
})
export class ProjectCalcService extends HttpService {
  breakDownSubj = new Subject<number>();
  formulaSubj = new Subject<number>();
  subTotalSubj = new Subject<number>();
  settingsSubj = new Subject<number>();
  switchAreaSubj = new Subject<number>();
  pricingNameSubj = new Subject<any>(); // id and name

  sumValueSubj = new Subject<SumValue>();

  // groupToUpdate = new Subject<PricingGroup>()

  // vol2
  groupToUpdate = new Subject<WsUpdateResponce<PricingGroupItem>>()

  miscItems: PricingMiscItem[];
  boxGrades: PricingGrade[];
  matGrades: PricingGrade[];

  miscItemsArr: string[];
  miscItemsMap = new Map<string, PricingPrice>();
  gradesItemsMap = new Map<string, PricingGrade>();

  miscGroupsMap = new Map<string, string[]>();

  boxGradesItemsStringArr: string[];
  finishMaterialItemsStringArr: string[];

  lastChosenPricingSubj = new Subject<ProjectPricing>();
  lastChosenPricingProjectId: number;

  constructor(
    protected injector: Injector,
    protected appStateStore: AppStateStore,
    protected appStateQuery: AppStateQuery,
  ) {
    super(injector);
  }

  countSelectedCells(table: Handsontable){
    const sumValObj = new SumValue()
    sumValObj.val = 0;
    sumValObj.qty = 0;

    const ranges = table.getSelectedRange();
    if (ranges) {
      ranges.forEach(range => {
        const datas = table.getData(range.from.row, range.from.col, range.to.row, range.to.col);

        datas.forEach(data => {
          const vals = data as any[]
          vals.forEach(val => {
            if (typeof val === 'number') {
              sumValObj.val += val
              sumValObj.qty += 1;
            }
          })
        })
      })
    }

    this.sumValueSubj.next(sumValObj)
  }


  getShortProject(projectId: number): Observable<Project> {
    return this.getData(`/project/${projectId}/schedule-info`, Project);
  }

  getFilteredPricing(filter: PricingFilter): Observable<PricingResponcePage> {
    return this.doHttpPost<PricingResponcePage>(`/pricing/list/filtered/short`, filter)
  }

  getDetailPricing(pricingId: number): Observable<ProjectPricing> {
    return this.getData(`/pricing/${pricingId}`, ProjectPricing);
  }

  getDetailPricingsForProject(projectId: number): Observable<ProjectPricing[]> {
    return this.getData(`/pricing/list/for-project/${projectId}`, ProjectPricing);
  }

  getShortPricingsForProject(projectId: number): Observable<ProjectPricing[]> {
    return this.getData(`/pricing/list/for-project/${projectId}/short`, ProjectPricing);
  }

  makeActivePricing(pricingId: number): Observable<ProjectPricing> {
    return this.putData(`/pricing/${pricingId}/activate`, {}, ProjectPricing);
  }

  updatePricing(pricing: ProjectPricing): Observable<ProjectPricing> {
    return this.putData(`/pricing`, pricing, ProjectPricing);
  }

  getPricingFilters(): Observable<PricingFilters> {
    return this.getData(`/pricing/list/filters`, PricingFilters);
  }

  createArea(pricingId: number, newArea: PricingArea): Observable<PricingArea> {
    return this.postData(`/pricing/${pricingId}/areas`, newArea, PricingArea);
  }

  getBreakDownTotals(pricingId: number, areaId: number): Observable<PricingGroup> {
    return this.getData(`/pricing/${pricingId}/areas/${areaId}/breakdown`, PricingGroup);
  }

  getFormulas(pricingId: number, areaId: number): Observable<PricingFormula> {
    return this.getData(`/pricing/${pricingId}/areas/${areaId}/formula`, PricingFormula);
  }

  getSubtotals(pricingId: number, areaId: number): Observable<PricingSubtotal> {
    return this.getData(`/pricing/${pricingId}/areas/${areaId}/totals`, PricingSubtotal);
  }

  getMiscItems(): Observable<PricingMiscItem[]> {
    return this.getData(`/pricing-settings/misc-groups/items`, PricingMiscItem);
  }

  getMiscItemsForProject(projectId): Observable<PricingMiscItem[]> {
    return this.getData(`/pricing-settings/misc/list?projectId=${projectId}`, PricingMiscItem);
  }

  getMiscItemsPresets(): Observable<PricingMiscItemPreset[]> {
    return this.getData(`/pricing-settings/modifiers/presets`, PricingMiscItemPreset);
  }

  getProposals(pricingId: number): Observable<ProposalsInfo> {
    return this.getData(`/pricing/${pricingId}/proposals`, ProposalsInfo);
  }

  getPricingOptionsForProject(projectId: number): Observable<PricingOptionShort[]> {
    return this.getData(`/pricing/list/for-project/${projectId}/short`, PricingOptionShort);
  }

  copyActivePricing(projectId: number): Observable<ProjectPricing> {
    return this.postData(`/pricing/list/for-project/${projectId}`, {}, ProjectPricing);
  }

  fillMiscGroups() {
    const allGroups = 'HINGE,SLIDE,PULL,DRAWER_BOX,CROWN_MOLDING,LIGHT_VALANCE';
    const typesMap = new Map();
    typesMap.set('HINGE', 'doorHinge')
    typesMap.set('SLIDE', 'drawerSlide')
    typesMap.set('PULL', 'doorPull')
    typesMap.set('DRAWER_BOX', 'drawerBox')
    typesMap.set('CROWN_MOLDING', 'crownMolding')
    typesMap.set('LIGHT_VALANCE', 'lightValance')

    this.getData(`/pricing-settings/misc-groups?types=${allGroups}`, PricingMiscItemGroup).subscribe(groups => {
      const typedGroups = groups as PricingMiscItemGroup[]

      typedGroups.forEach(group => {
        this.miscGroupsMap.set(typesMap.get(group.type), group.items.map(item => item.name))
      })
    })

    typesMap.set('INSTALLATION_TYPE', 'installType')
    this.doHttpGet<PricingMiscItemGroup[]>(`/pricing-settings/installation/types`).subscribe(items => {
      this.miscGroupsMap.set(typesMap.get('INSTALLATION_TYPE'), items.map(item => item.value))
      // typedGroups.forEach(group => {
      //   this.miscGroupsMap.set(typesMap.get(group.type), group.items.map(item => item.name))
      // })
    })
  }


  getThisMiscItems() {
    this.getData(`/pricing-settings/misc-groups/items`, PricingMiscItem).subscribe(items => {
      this.miscItems = items;

      this.updateThisMiscItems()
    })
  }

  updateThisMiscItems() {
    this.miscItemsArr = this.miscItems.map(item => item.name)
    for (const item of this.miscItems) {
      this.miscItemsMap.set(item.name, item)
    }
  }


  updateArea(pricingId: number, area: PricingArea): Observable<PricingArea> {
    return this.putData(`/pricing/${pricingId}/areas`, area, PricingArea)
  }

  updateGroup(pricingId: number, areaId: number, group: PricingGroup): Observable<PricingGroup> {
    return this.putData(`/pricing/${pricingId}/areas/${areaId}/groups`, group, PricingGroup)
  }

  deleteItemFromGroup(pricingId: number, areaId: number, groupId: number, itemId: number): Observable<PricingGroup> {
    return this.deleteData(`/pricing/${pricingId}/areas/${areaId}/groups/${groupId}/items/${itemId}`, PricingGroup)
  }

  getGrades(): Observable<PricingGrade[]> {
    return this.getData(`/pricing-settings/grades`, PricingGrade);
  }

  getArea(pricingId: number, areaId: number): Observable<PricingArea> {
    return this.getData(`/pricing/${pricingId}/areas/${areaId}`, PricingArea)
  }

  deleteArea(pricingId: number, areaId: number): Observable<PricingArea> {
    return this.deleteData(`/pricing/${pricingId}/areas/${areaId}`, PricingArea)
  }

  deletePricing(pricingId: number): Observable<ProjectPricing> {
    return this.deleteData(`/pricing/${pricingId}`, ProjectPricing)
  }

  // OPTIONS
  getSettings(pricingId: number, areaId: number): Observable<PricingSetting> {
    return this.getData(`/pricing/${pricingId}/areas/${areaId}/settings`, PricingSetting);
  }

  emitAllUpdates(areaId: number) {
    this.formulaSubj.next(areaId)
    this.settingsSubj.next(areaId)
    this.subTotalSubj.next(areaId)
    this.breakDownSubj.next(areaId)
  }

  saveProposal(pricingId: number, fileName: string): Observable<ProposalsInfo> {
    return this.postData(`/pricing/${pricingId}/pdf/save`, {fileName}, ProposalsInfo);
  }

  deleteContextMenus() {
    const elems = document.getElementsByClassName('htContextMenu')
    const elemsSize = elems.length
    // tslint:disable-next-line:prefer-for-of
    for (let i = elemsSize - 1; i >= 0; i--) {
      elems[i].remove()
    }
  }
}
